// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — DSA 1.8 vector layers on the immersive globe (headset, and a
 * phone's AR globe), drawn from what the browser's vector player computed
 * this frame (`VectorPlayer.frameLayers`): the same features at the same
 * point of their animations as on the browser globe.
 *
 * Children of the globe mesh, so they follow its rotation and scale with
 * no per-frame transform work:
 * - **Points** (quakes, alerts) are one instanced draw per layer: a small
 *   circle on the sphere for `deg`/`km` sizes (the disc the raster would
 *   draw, §7.2), each instance a 6 × 6 grid laid on the surface around its
 *   centre, so even a big ripple ring follows the curve. Fill, outline,
 *   ripple rings and halo are the same quad with a different fragment.
 *   Only the instance attributes change per frame.
 * - **Lines, tracks and polygon outlines** are ribbons on the surface, and
 *   **polygon fills** are triangulated in lon/lat (§4) and cut until every
 *   edge is under 2°, so they lie on the sphere. Both are rebuilt only on
 *   frames where something about them changed.
 *
 * `px` sizes (§7.2, XR): 0.0213° of visual angle — the CSS reference pixel
 * — at the distance the globe is seen from. The caller passes that as
 * radians of globe arc per px (`pxRad`), so a 3 px minimum stays 3 px
 * whether the user leans in or steps back.
 *
 * - **Labels** (`style.label`, vectorLabels.ts) are sprites facing the
 *   viewer, the same boxes as the browser's, beside the head or point and
 *   sized in XR px (2.5× the browser's, to be read in a headset), so they
 *   read the same at any distance.
 *
 * Depth-tested against the globe, so features on the far side are hidden.
 * A viewer standing inside the globe does not see them (the overlay
 * shells' inner twins are not repeated here).
 */
import type * as THREE from 'three'
import { kmPerDegOf, type CompiledVectorLayer, type LonLat, type VectorFeature } from './vector/vectorModel'
import { cutTrack, partialPath, subdivide, unwrap } from './vector/vectorGeo'
import { drawLabelBox, labelImageKey, labelsOf } from './vector/vectorLabels'
import { evalColor, evalNumber, type Rgba } from './vector/vectorStyle'
import type { VectorFrameLayer } from './vector/vectorPick'
import type { VectorDrawItem } from './vector/vectorTimeline'
import type { VectorSize } from './vector/vectorTypes'

const RAD = Math.PI / 180
const DEFAULT_COLOR: Rgba = [1, 0.55, 0.1, 1]
/** Over the overlay shells (≤ ~1.004) and under the geo-media dots. */
const LINE_LIFT = 1.0042
const FILL_LIFT = 1.004
const POINT_LIFT = 1.0046
/** Instance slots per point: its disc, two ripple rings or a halo. */
const SLOTS_PER_POINT = 3
const KIND_DISC = 0
const KIND_RING = 1
const KIND_HALO = 2
/** Ring outline width, px. */
const RING_PX = 1.5
/** Label boxes: just over the heads. */
const LABEL_LIFT = 1.006
/** Gap between a head (or point) and its label box, XR px. */
const LABEL_GAP_PX = 4
/**
 * Label boxes are this many times the browser's size in XR px: 12 px text is
 * 0.26° of visual angle, about 6 device px on a Quest; 2.5× is about 0.65°.
 */
const LABEL_XR_SIZE = 2.5
/** Label box pictures are drawn at this many canvas px per box px. */
const LABEL_SCALE = 3
/** Fill triangles are cut until every edge is under this, degrees. */
const FILL_EDGE_DEG = 2

/** 0.0213° of visual angle, the CSS reference pixel (§7.2 XR). */
export const XR_PX_ANGLE_RAD = 0.0213 * RAD

/**
 * Radians of globe arc that one XR px covers, seen from `distance` metres
 * off the globe's surface on a globe of `worldRadius` metres.
 */
export function pxRadAt(distance: number, worldRadius: number): number {
  return (Math.max(0.05, distance) * Math.tan(XR_PX_ANGLE_RAD)) / Math.max(1e-6, worldRadius)
}

/** Unit vector of a place, in the globe mesh's frame (vrGeoMedia's latLonToGlobeLocal). */
function dirOf(lon: number, lat: number, out: number[] = [0, 0, 0], at = 0): number[] {
  const cl = Math.cos(lat * RAD)
  out[at] = cl * Math.cos(lon * RAD)
  out[at + 1] = Math.sin(lat * RAD)
  out[at + 2] = -cl * Math.sin(lon * RAD)
  return out
}

/** Radians per unit of a size: px through `pxRad`, degrees, or km of the body's ground (`kmPerDeg`, DSA 1.9). */
function unitRad(size: VectorSize | undefined, pxRad: number, kmPerDeg: number): number {
  if (!size || size.units === 'px') return pxRad
  return size.units === 'deg' ? RAD : RAD / kmPerDeg
}

const POINT_VERTEX = /* glsl */ `
  attribute vec3 iDir;
  attribute float iRad;
  attribute vec4 iColor;
  attribute vec4 iStroke;
  attribute float iStrokeW;
  attribute float iKind;
  uniform float uLift;
  varying vec2 vUv;
  varying vec4 vColor;
  varying vec4 vStroke;
  varying float vEdge;
  varying float vKind;
  void main() {
    vec3 d = normalize(iDir);
    vec3 up = abs(d.y) > 0.999 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);
    vec3 e = normalize(cross(up, d));
    vec3 n = cross(d, e);
    float t = tan(min(iRad, 1.2));
    vec3 p = normalize(d + (position.x * e + position.y * n) * t) * uLift;
    vUv = position.xy;
    vColor = iColor;
    vStroke = iStroke;
    vEdge = iStrokeW / max(iRad, 1e-7);
    vKind = iKind;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  }
`

const POINT_FRAGMENT = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  varying vec4 vColor;
  varying vec4 vStroke;
  varying float vEdge;
  varying float vKind;
  void main() {
    float r = length(vUv);
    float aa = max(fwidth(r), 1e-4);
    if (r > 1.0 + aa) discard;
    float outer = 1.0 - smoothstep(1.0 - aa, 1.0 + aa, r);
    vec4 c;
    if (vKind < 0.5) {
      float edge = clamp(vEdge, 0.0, 1.0);
      float inStroke = edge > 0.0 ? smoothstep(1.0 - edge - aa, 1.0 - edge + aa, r) : 0.0;
      vec4 fill = vec4(vColor.rgb * vColor.a, vColor.a);
      vec4 stroke = vec4(vStroke.rgb * vStroke.a, vStroke.a);
      // The outline over the fill, as the browser draws circle-stroke.
      c = mix(fill, stroke + fill * (1.0 - stroke.a), inStroke) * outer;
    } else if (vKind < 1.5) {
      float w = clamp(vEdge, aa, 1.0);
      float a = smoothstep(1.0 - w - aa, 1.0 - w + aa, r) * outer * vColor.a;
      c = vec4(vColor.rgb * a, a);
    } else {
      float a = (1.0 - smoothstep(0.0, 1.0, r)) * vColor.a;
      c = vec4(vColor.rgb * a, a);
    }
    if (c.a < 0.004) discard;
    gl_FragColor = c;
  }
`

const SURFACE_VERTEX = /* glsl */ `
  attribute vec4 aColor;
  varying vec4 vColor;
  void main() {
    vColor = aColor;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

const SURFACE_FRAGMENT = /* glsl */ `
  precision highp float;
  varying vec4 vColor;
  void main() {
    if (vColor.a < 0.004) discard;
    gl_FragColor = vec4(vColor.rgb * vColor.a, vColor.a);
  }
`

interface PointPre {
  dir: [number, number, number]
  color: Rgba
  stroke: Rgba
  /** Diameter in size units. */
  size: number
  opacity: number
  z: number
}

interface LayerDraw {
  /** `dim`: the dataset's transparency and eye (owner, 2026-10-12), times every opacity here. */
  update(items: VectorDrawItem[], pxRad: number, dim: number): void
  /** Stacking: the layer's place in the DSA's `order`, bottom first. */
  setStack(i: number): void
  dispose(): void
}

export interface VrVectorLayersHandle {
  /**
   * Draws a frame. `version` is the player's frame counter: an unchanged
   * one with an unchanged `pxRad` costs nothing.
   */
  /** AYNI (owner, 2026-10-12): `dim` is the dataset's transparency and eye (1 opaque, 0 hidden). */
  update(frame: readonly VectorFrameLayer[], version: number, pxRad: number, dim?: number): void
  dispose(): void
}

export function createVrVectorLayers(THREE_: typeof THREE, globe: THREE.Object3D, globeRadius: number, lang: () => string = () => 'en'): VrVectorLayersHandle {
  const group = new THREE_.Group()
  group.name = 'vectorLayers'
  globe.add(group)
  const labelGroup = new THREE_.Group()
  labelGroup.name = 'vectorLabels'
  globe.add(labelGroup)
  /** Box pictures by picture key, with how many sprites use each. */
  const labelTextures = new Map<string, { texture: THREE.CanvasTexture; width: number; height: number; refs: number }>()
  /** Sprites by layer, then by feature. */
  const labelSprites = new Map<CompiledVectorLayer, Map<number, { sprite: THREE.Sprite; picture: string }>>()
  const draws = new Map<CompiledVectorLayer, LayerDraw>()
  let lastVersion = -1
  let lastPxRad = 0
  let lastDim = 1

  function premultipliedMaterial(vertexShader: string, fragmentShader: string, uniforms: Record<string, THREE.IUniform> = {}): THREE.ShaderMaterial {
    return new THREE_.ShaderMaterial({
      uniforms,
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE_.CustomBlending,
      blendEquation: THREE_.AddEquation,
      blendSrc: THREE_.OneFactor,
      blendDst: THREE_.OneMinusSrcAlphaFactor,
      premultipliedAlpha: true,
      side: THREE_.FrontSide,
    })
  }

  /** Per-feature style, evaluated once (§7). A null colour or size: not drawn. */
  function precompute(layer: CompiledVectorLayer): Map<number, PointPre> {
    const style = layer.spec.style
    const fields = layer.spec.fields
    const polygon = layer.spec.geometry === 'polygon'
    const sizeSpec = polygon ? style.stroke?.width : style.size
    const pre = new Map<number, PointPre>()
    for (const f of layer.features) {
      const color = evalColor(style.color, f.attrs, fields, DEFAULT_COLOR)
      const size = evalNumber(sizeSpec?.value, f.attrs, polygon ? 1 : 4)
      const opacity = evalNumber(style.opacity, f.attrs, 1)
      if (!color || size === null || opacity === null) continue
      const stroke = evalColor(style.stroke?.color, f.attrs, fields, [0, 0, 0, 0]) ?? [0, 0, 0, 0]
      const zf = style.zOrder?.field
      const zv = zf && typeof f.attrs[zf] === 'number' ? (f.attrs[zf] as number) : 0
      const p = f.point ?? [0, 0]
      pre.set(f.index, { dir: dirOf(p[0], p[1]) as [number, number, number], color, stroke, size, opacity, z: style.zOrder?.descending ? -zv : zv })
    }
    return pre
  }

  function pointLayer(layer: CompiledVectorLayer): LayerDraw {
    const style = layer.spec.style
    const pre = precompute(layer)
    const capacity = Math.max(1, layer.features.length * SLOTS_PER_POINT)
    const base = new THREE_.PlaneGeometry(2, 2, 6, 6)
    const geometry = new THREE_.InstancedBufferGeometry()
    geometry.index = base.index
    geometry.setAttribute('position', base.getAttribute('position'))
    const attr = (name: string, size: number) => {
      const a = new THREE_.InstancedBufferAttribute(new Float32Array(capacity * size), size)
      a.setUsage(THREE_.DynamicDrawUsage)
      geometry.setAttribute(name, a)
      return a
    }
    const aDir = attr('iDir', 3), aRad = attr('iRad', 1), aColor = attr('iColor', 4)
    const aStroke = attr('iStroke', 4), aStrokeW = attr('iStrokeW', 1), aKind = attr('iKind', 1)
    geometry.instanceCount = 0
    const material = premultipliedMaterial(POINT_VERTEX, POINT_FRAGMENT, { uLift: { value: globeRadius * POINT_LIFT } })
    const mesh = new THREE_.Mesh(geometry, material)
    mesh.frustumCulled = false
    group.add(mesh)
    const sizeSpec = style.size
    const minPx = sizeSpec?.minPx ?? 0
    const maxPx = sizeSpec?.maxPx ?? 1e6
    const strokePx = style.stroke?.width && style.stroke.width.units === 'px' && typeof style.stroke.width.value === 'number' ? style.stroke.width.value : 0
    const fillOpacity = style.fill?.opacity ?? 1
    const strokeOpacity = style.stroke?.opacity ?? 1
    return {
      update(items, pxRad, dim) {
        const layerOpacity = layer.layerOpacity * dim
        const k = unitRad(sizeSpec, pxRad, kmPerDegOf(layer))
        const sorted = items.filter((it) => pre.has(it.feature.index)).sort((a, b) => pre.get(a.feature.index)!.z - pre.get(b.feature.index)!.z)
        let n = 0
        const put = (p: PointPre, rad: number, color: Rgba, alpha: number, stroke: Rgba, strokeAlpha: number, strokeW: number, kind: number) => {
          if (n >= capacity || rad <= 0 || alpha <= 0.002) return
          aDir.array.set(p.dir, n * 3)
          aRad.array[n] = rad
          aColor.array.set([color[0], color[1], color[2], color[3] * alpha], n * 4)
          aStroke.array.set([stroke[0], stroke[1], stroke[2], stroke[3] * strokeAlpha], n * 4)
          aStrokeW.array[n] = strokeW
          aKind.array[n] = kind
          n++
        }
        for (const it of sorted) {
          const p = pre.get(it.feature.index)!
          // Clamped by minPx / maxPx first, then grown or shrunk (as the browser globe does).
          const r = Math.max((minPx * pxRad) / 2, Math.min((maxPx * pxRad) / 2, (k * p.size) / 2))
          const a = layerOpacity * p.opacity * it.opacity
          for (const e of it.emphasis) {
            if (e.effect === 'halo') put(p, r * (e.rings[0]?.scale ?? 1), p.color, a * (e.rings[0]?.opacity ?? 0) * 0.6, p.color, 0, 0, KIND_HALO)
          }
          put(p, r * it.size, p.color, a * fillOpacity, p.stroke, layerOpacity * strokeOpacity * it.opacity, strokePx * pxRad, KIND_DISC)
          for (const e of it.emphasis) {
            if (e.effect !== 'ripple') continue
            for (const ring of e.rings) put(p, r * ring.scale, p.color, layerOpacity * ring.opacity, p.color, 0, RING_PX * pxRad, KIND_RING)
          }
        }
        geometry.instanceCount = n
        for (const [a, size] of [[aDir, 3], [aRad, 1], [aColor, 4], [aStroke, 4], [aStrokeW, 1], [aKind, 1]] as const) {
          a.clearUpdateRanges()
          if (n > 0) a.addUpdateRange(0, n * size)
          a.needsUpdate = true
        }
      },
      setStack(i) {
        mesh.renderOrder = 9 + i * 3
      },
      dispose() {
        group.remove(mesh)
        geometry.dispose()
        base.dispose()
        material.dispose()
      },
    }
  }

  /** A ribbon along a lon/lat path, `halfW` radians each side, pushed into the buffers. */
  function ribbon(path: LonLat[], halfW: number, colours: Rgba[], pos: number[], col: number[], idx: number[]): void {
    const pts = path.map(([lon, lat]) => dirOf(lon, lat))
    if (pts.length < 2) return
    const start = pos.length / 3
    const v = new THREE_.Vector3(), t = new THREE_.Vector3(), s = new THREE_.Vector3(), q = new THREE_.Vector3()
    const lift = globeRadius * LINE_LIFT
    for (let i = 0; i < pts.length; i++) {
      v.fromArray(pts[i])
      const a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)]
      t.set(b[0] - a[0], b[1] - a[1], b[2] - a[2])
      s.crossVectors(v, t)
      if (s.lengthSq() < 1e-18) s.set(0, 1, 0).cross(v)
      s.normalize().multiplyScalar(Math.tan(halfW))
      q.copy(v).add(s).normalize().multiplyScalar(lift)
      pos.push(q.x, q.y, q.z)
      q.copy(v).sub(s).normalize().multiplyScalar(lift)
      pos.push(q.x, q.y, q.z)
      const c = colours[Math.min(colours.length - 1, i)]
      col.push(...c, ...c)
      if (i > 0) {
        const o = start + (i - 1) * 2
        idx.push(o, o + 1, o + 2, o + 1, o + 3, o + 2)
      }
    }
  }

  /** A filled small circle (a track's head), `r` radians around a place. */
  function disc(lon: number, lat: number, r: number, colour: Rgba, pos: number[], col: number[], idx: number[]): void {
    const d = new THREE_.Vector3().fromArray(dirOf(lon, lat))
    const up = Math.abs(d.y) > 0.999 ? new THREE_.Vector3(1, 0, 0) : new THREE_.Vector3(0, 1, 0)
    const e = new THREE_.Vector3().crossVectors(up, d).normalize()
    const n = new THREE_.Vector3().crossVectors(d, e)
    const lift = globeRadius * POINT_LIFT
    const centre = pos.length / 3
    const q = d.clone().multiplyScalar(lift)
    pos.push(q.x, q.y, q.z)
    col.push(...colour)
    const steps = 20
    const tr = Math.tan(r)
    for (let k = 0; k <= steps; k++) {
      const a = (k / steps) * Math.PI * 2
      q.copy(d).addScaledVector(e, Math.cos(a) * tr).addScaledVector(n, Math.sin(a) * tr).normalize().multiplyScalar(lift)
      pos.push(q.x, q.y, q.z)
      col.push(...colour)
      if (k > 0) idx.push(centre, centre + k, centre + k + 1)
    }
  }

  /** A polygon's fill triangles on the sphere, cut to ≤ 2° edges; cached per polygon. */
  const fillCache = new WeakMap<LonLat[][], Float32Array>()
  function fillTriangles(rings: LonLat[][]): Float32Array {
    const cached = fillCache.get(rings)
    if (cached) return cached
    const outer = subdivide(rings[0] ?? [])
    const holes = rings.slice(1).map((h) => subdivide(h))
    const strip = (r: LonLat[]) => (r.length > 1 && r[0][0] === r[r.length - 1][0] && r[0][1] === r[r.length - 1][1] ? r.slice(0, -1) : r)
    const contour = strip(outer).map(([x, y]) => new THREE_.Vector2(x, y))
    const holeVecs = holes.map((h) => strip(h).map(([x, y]) => new THREE_.Vector2(x, y)))
    const all = [...contour, ...holeVecs.flat()]
    const tris = contour.length >= 3 ? THREE_.ShapeUtils.triangulateShape(contour, holeVecs) : []
    const out: number[] = []
    const lift = globeRadius * FILL_LIFT
    const emit = (p: LonLat) => { const d = dirOf(p[0], p[1]); out.push(d[0] * lift, d[1] * lift, d[2] * lift) }
    // An edge's length on the sphere, roughly: a degree of longitude shrinks towards the poles.
    const len = (u: LonLat, w: LonLat) => Math.hypot((u[0] - w[0]) * Math.cos(((u[1] + w[1]) / 2) * RAD), u[1] - w[1])
    // Longest-edge bisection: a sliver across the map (a polar cap's fan) costs triangles in proportion to its area.
    const cut = (a: LonLat, b: LonLat, c: LonLat, depth: number) => {
      const ab = len(a, b), bc = len(b, c), ca = len(c, a)
      const longest = Math.max(ab, bc, ca)
      if (depth >= 24 || longest <= FILL_EDGE_DEG) { emit(a); emit(b); emit(c); return }
      const m = (u: LonLat, w: LonLat): LonLat => [(u[0] + w[0]) / 2, (u[1] + w[1]) / 2]
      if (longest === ab) { const p = m(a, b); cut(a, p, c, depth + 1); cut(p, b, c, depth + 1) }
      else if (longest === bc) { const p = m(b, c); cut(b, p, a, depth + 1); cut(p, c, a, depth + 1) }
      else { const p = m(c, a); cut(c, p, b, depth + 1); cut(p, a, b, depth + 1) }
    }
    for (const [i, j, k] of tris) {
      const p = (n: number): LonLat => [all[n].x, all[n].y]
      cut(p(i), p(j), p(k), 0)
    }
    const arr = new Float32Array(out)
    fillCache.set(rings, arr)
    return arr
  }

  function surfaceLayer(layer: CompiledVectorLayer): LayerDraw {
    const style = layer.spec.style
    const fields = layer.spec.fields
    const geometryKind = layer.spec.geometry
    const pre = precompute(layer)
    const sizeSpec = geometryKind === 'polygon' ? style.stroke?.width : style.size
    const minPx = sizeSpec?.minPx ?? 0
    const maxPx = sizeSpec?.maxPx ?? 1e6
    const fillOpacity = style.fill?.opacity ?? 1
    const strokeOpacity = style.stroke?.opacity ?? 1
    const material = premultipliedMaterial(SURFACE_VERTEX, SURFACE_FRAGMENT)
    const lineGeometry = new THREE_.BufferGeometry()
    const fillGeometry = new THREE_.BufferGeometry()
    const fillMesh = new THREE_.Mesh(fillGeometry, material)
    const lineMesh = new THREE_.Mesh(lineGeometry, material)
    for (const m of [fillMesh, lineMesh]) { m.frustumCulled = false; group.add(m) }
    let lastKey = ''

    const widthRad = (w: number, pxRad: number) => Math.max(minPx * pxRad, Math.min(maxPx * pxRad, w * unitRad(sizeSpec, pxRad, kmPerDegOf(layer))))

    return {
      update(items, pxRad, dim) {
        const pxBucket = Math.round(Math.log(pxRad) * 20)
        const key = `${pxBucket}|${dim}|` + items.map((it) => `${it.feature.index}:${it.opacity.toFixed(3)}:${it.size.toFixed(3)}:${it.draw.toFixed(3)}:${it.fill.toFixed(3)}:${geometryKind === 'track' ? `${it.headT}${it.head ? 'h' : ''}` : ''}`).join(',')
        if (key === lastKey) return
        lastKey = key
        const pos: number[] = [], col: number[] = [], idx: number[] = []
        const fpos: number[] = [], fcol: number[] = []
        const sorted = items.filter((it) => pre.has(it.feature.index)).sort((a, b) => pre.get(a.feature.index)!.z - pre.get(b.feature.index)!.z)
        for (const it of sorted) {
          const f: VectorFeature = it.feature
          const p = pre.get(f.index)!
          const a = layer.layerOpacity * dim * p.opacity * it.opacity
          const tint = (c: Rgba, alpha: number): Rgba => [c[0], c[1], c[2], c[3] * alpha]
          if (geometryKind === 'polygon') {
            for (const poly of f.polygons ?? []) {
              if (it.fill > 0 && fillOpacity > 0) {
                const tris = fillTriangles(poly)
                const c = tint(p.color, a * fillOpacity * it.fill)
                for (let i = 0; i < tris.length; i += 3) { fpos.push(tris[i], tris[i + 1], tris[i + 2]); fcol.push(...c) }
              }
              if (p.size > 0) {
                const half = widthRad(p.size, pxRad) / 2
                for (const ring of poly) {
                  const path = it.draw < 1 ? partialPath(subdivide(unwrap(ring)), it.draw) : subdivide(unwrap(ring))
                  ribbon(path, half, [tint(p.stroke, a * strokeOpacity)], pos, col, idx)
                }
              }
            }
          } else if (geometryKind === 'line') {
            const half = (widthRad(p.size, pxRad) * it.size) / 2
            for (const part of f.parts ?? []) {
              const path = subdivide(unwrap(part))
              ribbon(it.draw < 1 ? partialPath(path, it.draw) : path, half, [tint(p.color, a)], pos, col, idx)
            }
          } else if (geometryKind === 'track' && f.parts?.[0] && f.vertexTimes) {
            const track = style.track
            const window = track?.trail?.mode === 'window' ? track.trail.dataSeconds : undefined
            const cut = cutTrack(unwrap(f.parts[0]), f.vertexTimes, it.headT, track?.interpolate !== false, window)
            const perVertex = typeof style.color === 'object' && 'field' in style.color ? f.perVertex[style.color.field] : undefined
            const colourAt = (k: number): Rgba => {
              if (!perVertex) return tint(p.color, a)
              const c = evalColor(style.color, { ...f.attrs, [(style.color as { field: string }).field]: perVertex[cut.from[k]] }, fields, DEFAULT_COLOR)
              return tint(c ?? p.color, a)
            }
            // Each segment in the colour of the vertex it starts from (§7.6): runs of one colour, subdivided to ≤ 1°.
            const half = (widthRad(p.size, pxRad) * it.size) / 2
            let start = 0
            for (let k = 1; k <= cut.path.length; k++) {
              if (k < cut.path.length && colourAt(k).join() === colourAt(start).join()) continue
              const run = cut.path.slice(start, Math.min(k + 1, cut.path.length))
              if (run.length >= 2) ribbon(subdivide(run), half, [colourAt(start)], pos, col, idx)
              start = k
            }
            if (cut.head && track?.head && it.head) {
              const headSize = evalNumber(track.head.size?.value, f.attrs, p.size * 3) ?? p.size * 3
              const headUnits = track.head.size ?? sizeSpec
              const headMin = headUnits?.minPx ?? minPx
              const headMax = headUnits?.maxPx ?? maxPx
              const r = (Math.max(headMin * pxRad, Math.min(headMax * pxRad, headSize * unitRad(headUnits, pxRad, kmPerDegOf(layer)))) * it.size) / 2
              const hc = track.head.color ? evalColor(track.head.color, f.attrs, fields, DEFAULT_COLOR) : null
              disc(cut.head[0], cut.head[1], r, hc ? tint(hc, a) : colourAt(Math.max(0, cut.path.length - 1)), pos, col, idx)
            }
          }
        }
        lineGeometry.setAttribute('position', new THREE_.Float32BufferAttribute(pos, 3))
        lineGeometry.setAttribute('aColor', new THREE_.Float32BufferAttribute(col, 4))
        lineGeometry.setIndex(idx)
        fillGeometry.setAttribute('position', new THREE_.Float32BufferAttribute(fpos, 3))
        fillGeometry.setAttribute('aColor', new THREE_.Float32BufferAttribute(fcol, 4))
      },
      setStack(i) {
        fillMesh.renderOrder = 7 + i * 3
        lineMesh.renderOrder = 8 + i * 3
      },
      dispose() {
        group.remove(fillMesh)
        group.remove(lineMesh)
        lineGeometry.dispose()
        fillGeometry.dispose()
        material.dispose()
      },
    }
  }

  function releasePicture(picture: string): void {
    const t = labelTextures.get(picture)
    if (!t || --t.refs > 0) return
    t.texture.dispose()
    labelTextures.delete(picture)
  }

  function dropSprite(entry: { sprite: THREE.Sprite; picture: string }): void {
    labelGroup.remove(entry.sprite)
    entry.sprite.material.dispose()
    releasePicture(entry.picture)
  }

  /** One layer's label sprites for this frame. */
  function updateLabels(layer: CompiledVectorLayer, items: VectorDrawItem[], pxRad: number, dim: number): void {
    if (!layer.spec.style.label) return
    let sprites = labelSprites.get(layer)
    if (!sprites) { sprites = new Map(); labelSprites.set(layer, sprites) }
    const seen = new Set<number>()
    for (const l of labelsOf(layer, items, lang())) {
      const picture = labelImageKey(l)
      let entry = sprites.get(l.index)
      if (entry && entry.picture !== picture) { dropSprite(entry); sprites.delete(l.index); entry = undefined }
      let tex = labelTextures.get(picture)
      if (!entry) {
        if (!tex) {
          const box = drawLabelBox(l, LABEL_SCALE)
          if (!box) continue
          const texture = new THREE_.CanvasTexture(box.canvas as HTMLCanvasElement)
          texture.colorSpace = THREE_.SRGBColorSpace
          texture.generateMipmaps = false
          texture.minFilter = THREE_.LinearFilter
          tex = { texture, width: box.width, height: box.height, refs: 0 }
          labelTextures.set(picture, tex)
        }
        tex.refs++
        const material = new THREE_.SpriteMaterial({ map: tex.texture, transparent: true, depthTest: true, depthWrite: false })
        const sprite = new THREE_.Sprite(material)
        sprite.renderOrder = 60
        labelGroup.add(sprite)
        entry = { sprite, picture }
        sprites.set(l.index, entry)
      }
      tex = labelTextures.get(picture)!
      seen.add(l.index)
      const unit = pxRad * globeRadius
      const clearPx = l.clear
        ? Math.max(l.clear.size?.minPx ?? 0, Math.min(l.clear.size?.maxPx ?? 1e6, (l.clear.value * unitRad(l.clear.size, pxRad, kmPerDegOf(layer))) / pxRad)) / 2
        : 0
      const d = dirOf(l.lon, l.lat)
      entry.sprite.position.set(d[0], d[1], d[2]).multiplyScalar(globeRadius * LABEL_LIFT)
      entry.sprite.scale.set(tex.width * LABEL_XR_SIZE * unit, tex.height * LABEL_XR_SIZE * unit, 1)
      // The box's left edge sits right of the head: centre.x below 0 moves it right, in box widths.
      entry.sprite.center.set(-(clearPx + LABEL_GAP_PX) / (tex.width * LABEL_XR_SIZE), 0.5)
      // The dataset's transparency and eye reach the boxes too.
      entry.sprite.material.opacity = l.opacity * dim
      entry.sprite.visible = dim > 0
    }
    for (const [index, entry] of sprites) {
      if (seen.has(index)) continue
      dropSprite(entry)
      sprites.delete(index)
    }
  }

  function clearLabels(layer: CompiledVectorLayer): void {
    const sprites = labelSprites.get(layer)
    if (!sprites) return
    for (const entry of sprites.values()) dropSprite(entry)
    labelSprites.delete(layer)
  }

  return {
    update(frame, version, pxRad, dim = 1) {
      if (version === lastVersion && Math.abs(pxRad - lastPxRad) <= lastPxRad * 0.02 && dim === lastDim) return
      lastVersion = version
      lastPxRad = pxRad
      lastDim = dim
      const wanted = new Set(frame.map((l) => l.layer))
      for (const [layer, draw] of draws) {
        if (!wanted.has(layer)) { draw.dispose(); draws.delete(layer) }
      }
      for (const layer of [...labelSprites.keys()]) if (!wanted.has(layer)) clearLabels(layer)
      frame.forEach(({ layer, items }, i) => {
        let draw = draws.get(layer)
        if (!draw) {
          draw = layer.spec.geometry === 'point' ? pointLayer(layer) : surfaceLayer(layer)
          draws.set(layer, draw)
        }
        draw.setStack(i)
        draw.update(items, pxRad, dim)
        updateLabels(layer, items, pxRad, dim)
      })
    },
    dispose() {
      for (const d of draws.values()) d.dispose()
      draws.clear()
      for (const layer of [...labelSprites.keys()]) clearLabels(layer)
      globe.remove(group)
      globe.remove(labelGroup)
    },
  }
}
